import { trackTemplatePlay } from '@anm/analytic';
import { TemplateMeta, TemplateSize } from '@anm/api/modules/templates';
import useIsMountedCheck from '@anm/hooks/useIsMountedCheck';
import { createContext, useCallback, FC } from 'react';
import Loadable from 'react-loadable';
import { useSelector } from 'react-redux';

import { selectCurrentTemplate, selectMediumSize, selectSmallSize } from '../../../store/modules/template/selectors';
import { useRefWithUpdateOnResize } from '../TemplateVideoPlayer/hooks';
import VideoRatioList from '../VideoRatioList';
import { VideoRatioItemProps } from '../VideoRatioList/types';

import VideoLoadingPreview from './VideoLoadingPreview';
import TemplateVideoPlayerWrapper, {
  PlayerAndPreviewWrapper,
  TemplateVideoPlayerWithRatioList,
  TemplateVideoPlayerWithRatioListWrapper
} from './Wrapper';
import { useChangeFormatUrl, useTemplateVideoPlayer } from './hooks';
import { TemplateVideoPlayerProps, TemplateVideoPlayerPropsWithoutHandlers } from './types';

export * from './types';

const VideoPlayer = Loadable({
  loader: () => import('@anm/components/VideoPlayer'),
  loading: () => null
});

const { Consumer: PreviewConsumer, Provider: PreviewProvider } = createContext<TemplateSize | null>(null);

export const TemplatePreviewProvider = PreviewProvider;

const videoOptions = {
  autoPlay: false,
  controls: true
};

const TemplateVideoPlayer: FC<TemplateVideoPlayerProps> = ({
  variant,
  direction,
  videoRef,
  templateKind,
  playerRef,
  onChangeRatio,
  activeRatioItemColor,
  ...noHandlersProps
}) => {
  const isMounted = useIsMountedCheck();
  const template = useSelector(selectCurrentTemplate);
  const {
    setIsPlayEventSent,
    setIsVideoLoaded,
    isPlayEventSent,
    isVideoLoaded,
    setVideoData,
    videoData
  } = useTemplateVideoPlayer(noHandlersProps.currentRatioItem);
  useChangeFormatUrl(noHandlersProps.currentRatioItem);

  const videoWrapperRef = useRefWithUpdateOnResize<HTMLDivElement>();

  const handleVideoPlay = useCallback(() => {
    if (!template) return;
    const { templateId, displayName } = template;

    setIsVideoLoaded(true);
    setIsPlayEventSent(true);
    !isPlayEventSent && trackTemplatePlay(displayName, templateId);
  }, [isPlayEventSent, template]);

  const handleVideoLoadStart = () => setIsVideoLoaded(false);

  const handleChange = useCallback((ratioItem: VideoRatioItemProps) => {
    onChangeRatio(ratioItem);
    setVideoData({
      activeRatioName: ratioItem.name,
      videoUrl: selectSmallSize(ratioItem.video).url,
      poster: selectMediumSize(ratioItem.thumb).url
    });
  }, []);

  const { poster, videoUrl, activeRatioName } = videoData;

  return (
    <TemplateVideoPlayerWithRatioListWrapper {...(noHandlersProps as TemplateVideoPlayerPropsWithoutHandlers)}>
      <TemplateVideoPlayerWithRatioList {...(noHandlersProps as TemplateVideoPlayerPropsWithoutHandlers)}>
        <VideoRatioList
          onChange={handleChange}
          {...{
            variant,
            direction,
            ...((template && template.meta) as TemplateMeta),
            activeRatioItemColor,
            activeRatioName
          }}
        />
        <TemplateVideoPlayerWrapper
          ref={videoWrapperRef}
          maxHeight={videoWrapperRef.current?.clientHeight}
          {...(noHandlersProps as TemplateVideoPlayerPropsWithoutHandlers)}
        >
          <PreviewConsumer>
            {previewFromUrl => (
              <PlayerAndPreviewWrapper>
                <VideoLoadingPreview
                  src={(previewFromUrl && previewFromUrl.url) || poster}
                  isVisible={!isMounted || !isVideoLoaded}
                />
                {isMounted && templateKind !== 'image' && (
                  <VideoPlayer
                    src={videoUrl}
                    poster={(previewFromUrl && previewFromUrl.url) || poster}
                    options={videoOptions}
                    videoRef={videoRef}
                    playerRef={playerRef}
                    onVideoPlay={handleVideoPlay}
                    onVideoLoadStart={handleVideoLoadStart}
                  />
                )}
              </PlayerAndPreviewWrapper>
            )}
          </PreviewConsumer>
        </TemplateVideoPlayerWrapper>
      </TemplateVideoPlayerWithRatioList>
    </TemplateVideoPlayerWithRatioListWrapper>
  );
};

export default TemplateVideoPlayer;
